// sapi-admin/src/index.js
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// ════════════════════════════════════════════════════════════════════════
// ✅ GESTIONNAIRE GLOBAL DES ERREURS D'ABORT
// ════════════════════════════════════════════════════════════════════════

// 1. Intercepter les promesses rejetées (unhandledrejection)
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  if (
    error?.message?.includes('aborted') ||
    error?.message?.includes('AbortError') ||
    error?.name === 'AbortError'
  ) {
    event.preventDefault();
    console.debug('ℹ️ AbortError ignorée (unhandledrejection)');
    return;
  }
  console.error('❌ Erreur non capturée:', error);
});

// 2. Intercepter les erreurs de console (pour les ignorer)
const originalConsoleError = console.error;
console.error = function (...args) {
  const message = args[0]?.message || args[0] || '';
  if (typeof message === 'string') {
    if (
      message.includes('aborted') ||
      message.includes('AbortError') ||
      message.includes('user aborted')
    ) {
      console.debug('ℹ️ AbortError ignorée (console.error)');
      return;
    }
  }
  originalConsoleError.apply(console, args);
};

// 3. Intercepter les erreurs de fetch
const originalFetch = window.fetch;
window.fetch = function (...args) {
  return originalFetch.apply(this, args).catch((error) => {
    if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
      console.debug('ℹ️ Fetch AbortError ignorée');
      return new Response(null, { status: 499, statusText: 'Aborted' });
    }
    throw error;
  });
};

// ════════════════════════════════════════════════════════════════════════
// ERREUR BOUNDARY
// ════════════════════════════════════════════════════════════════════════
class SafeErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError(error) {
    if (
      error?.message?.includes('aborted') ||
      error?.message?.includes('AbortError') ||
      error?.name === 'AbortError'
    ) {
      return { hasError: false };
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    if (
      error?.message?.includes('aborted') ||
      error?.message?.includes('AbortError') ||
      error?.name === 'AbortError'
    ) {
      console.debug('ℹ️ AbortError capturée par ErrorBoundary');
      return;
    }
    console.error('❌ Crash critique:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Une erreur est survenue.</h2>
          <button onClick={() => window.location.reload()}>Actualiser</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ════════════════════════════════════════════════════════════════════════
// ENREGISTREMENT DU SERVICE WORKER (pour les notifications push)
// ════════════════════════════════════════════════════════════════════════
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  navigator.serviceWorker
    .register('/firebase-messaging-sw.js')
    .then((registration) => {
      console.log('✅ Firebase Messaging SW enregistré:', registration.scope);
    })
    .catch((error) => {
      console.error('❌ Échec Firebase Messaging SW:', error);
    });
}

// ════════════════════════════════════════════════════════════════════════
// RENDU PRINCIPAL
// ════════════════════════════════════════════════════════════════════════
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <SafeErrorBoundary>
      <App />
    </SafeErrorBoundary>
  </React.StrictMode>
);

// Si vous voulez mesurer les performances (optionnel)
reportWebVitals();